import { ServiceMode } from 'generated/graphql-gateway';
import { ISystemwideOffersFragment, ISystemwideTierGroupFragment } from 'generated/sanity-graphql';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';

import {
  OfferWithPrice,
  SystemwideOffer,
  SystemwideTierGroup,
  getPriceTargets,
  selectOffer,
  validateTierGroup,
} from './base';

function getEntryPrice(
  entryId: string | undefined,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!entryId) {
    return 0;
  }
  const menuEntry = indexedMenu.find(entryId) as ILocalizedMenuEntry | undefined;
  return menuEntry?.price?.[posServiceMode] ?? 0;
}

function getMenuItemsPrice(
  tierGroup: SystemwideTierGroup,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!tierGroup.menu_items || !tierGroup.menu_items.length) {
    return 0;
  }

  let totalPrice = 0;
  for (const menuItem of tierGroup.menu_items) {
    // menuItem is IComboPlusFragment | IItemFragment, which should have an _id
    if (menuItem?._id) {
      const price = getEntryPrice(menuItem._id, indexedMenu, posServiceMode);
      if (typeof price === 'number' && !isNaN(price)) {
        totalPrice += price;
      }
    }
  }
  return totalPrice;
}

function getOfferPrice(
  offer: ISystemwideOffersFragment,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): number {
  if (!offer?._id) {
    return 0;
  }
  const price = getEntryPrice(offer._id, indexedMenu, posServiceMode);
  if (typeof price !== 'number' || isNaN(price)) {
    return 0;
  }
  return price;
}

export function processTierGroup_UnifiedMenu(
  tierGroup: ISystemwideTierGroupFragment,
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode
): SystemwideOffer | null {
  const [validatedTierGroup] = validateTierGroup(tierGroup);
  if (!validatedTierGroup) {
    return null;
  }

  const itemsPrice = getMenuItemsPrice(validatedTierGroup, indexedMenu, posServiceMode);
  const priceTargets = getPriceTargets(validatedTierGroup, itemsPrice);

  const offersWithPrice: OfferWithPrice[] = validatedTierGroup.offers
    .map(offer => {
      const price = getOfferPrice(offer, indexedMenu, posServiceMode);
      return price >= 0 ? { offer, price } : null;
    })
    .filter((item): item is OfferWithPrice => item !== null);

  const [selectedOffer] = selectOffer(priceTargets, offersWithPrice);

  return selectedOffer;
}

export type TransformTierGroupsToOffersOptions_UnifiedMenu = {
  items: (ISystemwideOffersFragment | ISystemwideTierGroupFragment)[];
  indexedMenu: IndexedMenu;
  serviceMode: ServiceMode;
};

export function transformTierGroupsToOffers_UnifiedMenu({
  items,
  indexedMenu,
  serviceMode,
}: TransformTierGroupsToOffersOptions_UnifiedMenu): ISystemwideOffersFragment[] {
  if (!items.length) {
    return [];
  }

  const posServiceMode: PosServiceMode = serviceMode === 'DELIVERY' ? 'delivery' : 'pickup';

  const legacyOfferIdsToRemove = new Set<string>();

  const transformed = items
    .map(item => {
      if (item.__typename !== 'SystemwideTierGroup') {
        return item; // Pass through regular offers
      }

      const selectedOffer = processTierGroup_UnifiedMenu(item, indexedMenu, posServiceMode);

      // Collect legacy offer IDs to remove
      if (item.legacyOffers?.length) {
        item.legacyOffers.forEach(legacyOffer => {
          if (legacyOffer?._id) {
            legacyOfferIdsToRemove.add(legacyOffer._id);
          }
        });
      }

      return selectedOffer;
    })
    .filter((offer): offer is ISystemwideOffersFragment => offer !== null)
    // Remove legacy offers that were replaced by tier group selections
    .filter(offer => !legacyOfferIdsToRemove.has(offer._id));

  return transformed;
}
